// 千日手の軍配者たち (hollow-actor の作り直し) をゲーム本体の一戦にする (2026-10-29・開発版の `?fight=hollow-actor` だけ)。
//   技と挟み撃ちの決まりは src/twins_proxy_plan.ts、動きの元はラボ src/twins_proxy_lab.ts (twins-proxy-lab.html)。
//   ゲームはボスを1体 (state.boss) しか持たないので、二人と装置はこの部品が持ち、state.boss は「いま斬れる所」を指す影にする:
//     - boss.x = 主人公に一番近い立っている方 (3部屋目は斬れる部品の位置。無い時は遠くへ置いて当たらないようにする)。
//     - boss.hp = 床 + 二人 (3部屋目は装置) の残り。二人はそれぞれ部屋の幅の半分を持ち、使い切ると膝をついて攻撃をやめる。
//       二人とも倒れると hp が床に届き、普段の崩れ → 致命になる (二人まとめて1回)。致命の後の部屋で二人とも立ち直る。
//     - boss.posture = 二人で1本の体幹。満ちると崩れる (2.6秒・この間は削りが3倍)。崩れても致命にはならない (hp が残る間)。
//   二人の当たりは本物の当たり (ActiveHit) にして updateHits へ渡す＝パリィ・回避・被弾は他のボスと同じ仕組みを通る。
//   崩れて致命を待つ間・致命の間・撃破の後は、今の updateBoss をそのまま通す (崩れからの戻り・致命・とどめを使い回す)。
//   部屋: 1 = 二人 / 2 = 二人を速く (作者 t5「同じ技でテンポを上げる」。不安定にする案は答えるページで出す) / 3 = 奥の装置。
//   FX と音は仮 (作者 c1)。絵は二人・装置とも描いた絵が来るまで、図形の代わり (src/twins_fight_draw.ts)。
import type { ActiveHit, Boss, Player, ScreenShake, SfxId } from './types'
import { ARENA, arenaBoundsFor } from './balance'
import { BOSS_DEFINITIONS } from './bosses'
import { chipFloor, roomSize } from './combat'
import { TWINS_REVEAL_SFX_PEAK_MS, twinsSfxDevice, twinsSfxHit, twinsSfxReveal, twinsSfxTell } from './boss_sfx_v1'
import { BULLET_SPEED, SAME_BEAT_MS, absHits, earliestStart, pickable, redGlowFrom, type BookedHit, type TwinId, type TwinMove } from './twins_proxy_plan'

export const TWINS_BOSS_ID = 'hollow-actor'
export const TWINS_NAME = '千日手の軍配者たち'
export const TWINS_ROOM_COUNT = 3
let enabled = false
/** 開発版の一戦にする (main.ts が `?fight=hollow-actor` の時に呼び、その後でボスを作り直す)。
 *  名前と部屋の数だけ書き換える。技の一覧 (unlockByExecutes) は使わない (二人の技はこの部品が出す)。 */
let original: { name: string; title: typeof BOSS_DEFINITIONS[number]['title']; roomCount: typeof BOSS_DEFINITIONS[number]['roomCount'] } | null = null
export function useTwinsFight(on = true): void {
  enabled = on; fight = null
  const def = BOSS_DEFINITIONS.find((b) => b.id === TWINS_BOSS_ID)!
  if (!on) {
    if (original) { def.name = original.name; def.title = original.title; def.roomCount = original.roomCount; original = null }
    return
  }
  original ??= { name: def.name, title: def.title, roomCount: def.roomCount }
  def.name = TWINS_NAME
  def.title = 'The Sennichite Tacticians'
  def.roomCount = TWINS_ROOM_COUNT
}
export function twinsFightOn(boss: Boss | null | undefined): boolean {
  return enabled && !!boss && boss.id === TWINS_BOSS_ID
}

/** 二人の部屋の背景 (灰の城の一室・10-30 作者 c1「この城の一角、ひと部屋」)。開発版の二人の戦いの時だけ。 */
export const TWINS_BG_SRC = '/bg/twins-room.png'
export function twinsBgSrc(bossId: string | undefined): string | undefined {
  return enabled && bossId === TWINS_BOSS_ID ? TWINS_BG_SRC : undefined
}

/** 崩れの長さと、崩れの間の削りの倍率 (ラボと同じ)。 */
export const TWINS_BREAK_MS = 2600
export const TWINS_BREAK_DAMAGE_MUL = 3
/** 主人公の一太刀で減る割合 (ラボの hp60 に 7 = 1人ぶんの約 1/9)。本物の攻撃力は使わず、この割合で揃える。 */
export const TWIN_HIT_SHARE = 7 / 60
/** 跳び越えの速さ・短くても見せる長さ・高さ (px)。立ち位置で二人・倒れた体と空ける間。 */
export const HOP_SPEED = 620
export const HOP_MIN_MS = 520
export const HOP_HEIGHT = 170
export const TWIN_GAP = 120
/** 跳び越えの今の高さ (描画用・床から上へ)。 */
export function hopLift(t: TwinState, now: number): number {
  if (!t.hop) return 0
  const u = Math.min(1, Math.max(0, (now - t.hop.start) / t.hop.dur))
  return 4 * HOP_HEIGHT * u * (1 - u)
}
/** 2部屋目の速さ (技の時間をこの倍に縮める)。 */
export const ROOM2_TEMPO = 0.82

const DMG = { parry: 12, dodge: 22, bullet: 8, device: 20 } as const

// ---- 案A (11-03 作者の答え・開発版 ?twinsPlanA=1 だけ。配布版の既定は変えない) ----
//   作者の答え: 「A 倒れは一時・二人倒れたら『総崩れ』・致命は体幹だけ」「A 総崩れ：二人とも数秒動けず、斬るほど体幹が貯まる」。
//   - 片方を削り切ると倒れるが、数秒で起き上がる (体力を少し戻して)。
//   - 二人とも倒れたら総崩れ: 二人とも動けず、斬るたびに体幹が大きく貯まり、戻りも止まる。終わると二人とも起き上がる。
//   - 致命は体幹が満ちた時だけ (体力が床に届いても致命にならない)。体幹で崩れたら、2.6秒の崩れではなくそのまま致命へ。
//   - 弾き返した鈴は体幹だけを削る (体力は削らない)。
//   1・2部屋目だけ。3部屋目の装置は今のまま (答えるページで聞く)。秒数と割合は仮 (数値は技が揃ってから決める)。
let planA = false
export function setTwinsPlanA(on: boolean): void { planA = on; fight = null }
export function twinsPlanA(): boolean { return planA }
export const PLAN_A = {
  /** 片方が倒れている長さ (ms)。 */
  downMs: 4000,
  /** 起き上がる時に戻す体力 (その一人の持ち分に対する割合)。 */
  riseBack: 0.4,
  /** 総崩れの長さ (ms)。 */
  collapseMs: 4000,
  /** 総崩れの間、一太刀で足す体幹 (最大に対する割合)。 */
  collapsePosture: 0.15,
}
/** 弾き返した鈴が一発で削る体力 (hits.ts REFLECTED_PROJECTILE_BOSS_HIT_DRAFT.damage と同じ)。案A ではこの分を戻す。 */
const REFLECTED_BELL_HP = 6

export interface TwinState {
  id: TwinId; x: number; face: 1 | -1
  pool: number; lost: number; down: boolean
  /** 案A: 倒れた一人が起き上がる時刻 (ms)。0 = 起き上がらない (配布版)。 */
  downUntil: number
  move: TwinMove | null; start: number; booked: BookedHit[]; done: Set<number>; nextThink: number
  fromX: number; lunge: number; crossing: boolean; told: boolean
  /** 反対側へ回る時の跳び越え (主人公の頭の上を弧で越える。体を通り抜けて重ならないように・10-30)。 */
  hop: { from: number; start: number; dur: number } | null
}
export type DevKind = 'march' | 'wall' | 'map' | 'curtain' | 'light'
export const DEV_ORDER: DevKind[] = ['march', 'wall', 'map', 'curtain', 'light']
export interface DevPiece { x: number; out: number; hitId: number; spawned: boolean }
export interface DevAtk {
  kind: DevKind; start: number; end: number; side: -1 | 1; x: number; hit: boolean
  pieces: DevPiece[]; wallHp: number; wallUntil: number; lightX: number
}
export interface TwinsFight {
  room: 1 | 2 | 3; roomKey: number; lastNow: number
  twins: TwinState[]
  brokenUntil: number
  /** 案A: 総崩れが終わる時刻 (ms)。0 = 総崩れではない。 */
  collapseUntil: number
  /** 案A: 前のコマに飛んでいた弾き返した鈴 (id → x)。消えた鈴がボスに当たったかを数える。 */
  bells: Map<number, number>
  devPool: number; devLost: number; dev: DevAtk | null; devNext: number; devIdx: number
  pending: ActiveHit[]
  /** 弾いた時に装置を削る行軍の駒の当たり (id)。 */
  marchIds: Set<number>
  /** いま斬れる所 (boss.x を置く先)。null = 斬れる所が無い。 */
  target: { kind: 'twin'; id: TwinId } | { kind: 'wall' | 'map' } | null
  /** 3部屋目の現れ (2部屋目の致命の後だけ)。倒れた二人から光が浮かび、画面の中央へ集まって、装置が突然現れる。 */
  reveal: { start: number; bodies: { id: TwinId; x: number; face: 1 | -1 }[] } | null
}
let fight: TwinsFight | null = null
export function twinsFightState(): TwinsFight | null { return fight }

let nextId = 700000
// 技の選び・考える間・壁の位置の乱数。 テストは種のある乱数に差し替えて、 毎回同じ流れで測る (11-01・全体テストの時だけ位置取りのテストが揺れたため)。
let rnd: () => number = Math.random
export function setTwinsRandom(r: (() => number) | null): void { rnd = r ?? Math.random }

function walls(boss: Boss) {
  const w = arenaBoundsFor(boss.id)
  return { left: w.leftWall + 40, right: w.rightWall - 40, mid: (w.leftWall + w.rightWall) / 2, width: w.rightWall - w.leftWall }
}

function roomOf(boss: Boss): 1 | 2 | 3 {
  return boss.executesTaken <= 0 ? 1 : boss.executesTaken === 1 ? 2 : 3
}

/** 3部屋目の現れの時間割 (ms・10-30 作者「2幕のちめいをいれてから光を浮上させた後、画面中央に光が集中して突如として3幕目が現れる」)。
 *  倒れた二人を残す → 体から光が浮かび体が薄れる → 光が画面の中央へ集まる → 装置が突然現れる → 光が消える。 */
export const REVEAL = { rise: 700, gather: 1900, appear: 2700, end: 3400 } as const
/** 3部屋目の現れの比較版 C「上から落ちる重さ」(10-30 夜 作者が答えるページで C を選んだ・開発版 ?twinsReveal=c)。
 *  部屋が暗くなる → 倒れた二人から光が浮かぶ → 光が装置の真上の一点へ吸い込まれて空気がゆがむ → 光が消え床に影が広がる
 *  → 装置が真上から落ちて床を割る (appear) → 灰と砂ぼこりが吹き荒れて、暗さが引く (end)。 */
const REVEAL_DROP_BASE = { dark: 200, rise: 400, gather: 1500, swallow: 2150, fall: 2450, appear: 2680, end: 4600 } as const
/** 溜め (光が呑まれてから装置が落ち始めるまで) を伸ばした時は fall 以降がずれる。setTwinsRevealHold で書き換える。 */
export const REVEAL_DROP: { -readonly [K in keyof typeof REVEAL_DROP_BASE]: number } = { ...REVEAL_DROP_BASE }
/** 配布版の既定の溜め (11-01 作者が +1.0 秒を選んだ)。 */
export const REVEAL_HOLD_DEFAULT = 1000
/** C の溜めを伸ばす比較版 (10-31 作者「落ちてくるまでの間がもう少し欲しい」・開発版 &twinsRevealHold=300|600|1000 ms)。 */
export function setTwinsRevealHold(ms: number): void {
  const h = Number.isFinite(ms) ? Math.max(0, Math.min(2000, ms)) : 0
  REVEAL_DROP.fall = REVEAL_DROP_BASE.fall + h; REVEAL_DROP.appear = REVEAL_DROP_BASE.appear + h; REVEAL_DROP.end = REVEAL_DROP_BASE.end + h
}
setTwinsRevealHold(REVEAL_HOLD_DEFAULT)
export type TwinsRevealLook = 'light' | 'drop'
// 既定は C「上から落ちる重さ」(11-01 作者が選んだ)。前の「光が集まって現れる」版は開発版 ?twinsReveal=old で残す。
let revealLook: TwinsRevealLook = 'drop'
export function setTwinsRevealLook(v: string | null): void { revealLook = v === 'old' ? 'light' : 'drop' }
export function twinsRevealLook(): TwinsRevealLook { return revealLook }
/** 装置が現れる (落ちて床に着く) 時刻。 */
export function revealAppearMs(): number { return revealLook === 'drop' ? REVEAL_DROP.appear : REVEAL.appear }
let revealShakeAt: number | null = null
/** 落ちた装置が床を割った時の揺れ (1回だけ渡す・強くしすぎない)。game.ts が毎コマ聞く。 */
export function takeTwinsRevealShake(nowSec: number): ScreenShake | null {
  if (revealShakeAt === null) return null
  revealShakeAt = null
  return { remainingSec: 0.34, amplitude: 7, startAmplitude: 7, totalDur: 0.34, startSec: nowSec }
}

function setup(boss: Boss, player: Player, now: number, prev: TwinsFight | null): TwinsFight {
  const room = roomOf(boss)
  // 2部屋目の致命から続けて入った時だけ現れを見せる (開発用に部屋へ飛んだ時・やり直しは装置をすぐ出す)。
  const reveal = room === 3 && prev?.room === 2 && prev.roomKey === boss.executesTaken - 1
    ? { start: now, bodies: prev.twins.map((t) => ({ id: t.id, x: t.x, face: t.face })) } : null
  const span = Math.max(1, roomSize(boss) - chipFloor(boss))
  const w = walls(boss)
  const mk = (id: TwinId, x: number): TwinState => ({
    id, x, face: 1, pool: span / 2, lost: 0, down: false, downUntil: 0, move: null, start: 0, booked: [], done: new Set(),
    nextThink: now + 900 + (id === 'imo' ? 500 : 0), fromX: x, lunge: 0, crossing: false, told: false, hop: null,
  })
  const left = Math.max(w.left, player.x - 260), right = Math.min(w.right, player.x + 260)
  return {
    room, roomKey: boss.executesTaken, lastNow: now,
    twins: room === 3 ? [] : [mk('ane', left), mk('imo', right)],
    brokenUntil: 0, collapseUntil: 0, bells: new Map(), devPool: span, devLost: 0, dev: null, devNext: now + (reveal ? revealAppearMs() : 0) + 1200, devIdx: 0,
    pending: [], marchIds: new Set(), target: null, reveal,
  }
}

/** 部屋が変わった・やり直した時に作り直す。 */
function ensure(boss: Boss, player: Player, now: number): TwinsFight {
  // やり直し (時刻が戻る・hp が満タンへ戻る) も作り直す。
  const refilled = !!fight && fight.roomKey === boss.executesTaken && boss.hp > chipFloor(boss) + remainOf(fight) + 1
  if (!fight || fight.roomKey !== boss.executesTaken || now < fight.lastNow || refilled) fight = setup(boss, player, now, fight && !refilled && now >= fight.lastNow ? fight : null)
  return fight
}

/** 描く前に呼ぶ。名乗りの間 (まだ一度も動いていない) も二人を立たせ、前の総集編の絵を出さない。 */
export function twinsPrepare(boss: Boss, player: Player, nowSec: number): TwinsFight { return ensure(boss, player, nowSec * 1000) }

function remainOf(f: TwinsFight): number {
  return f.room === 3 ? f.devPool - f.devLost : f.twins.reduce((s, t) => s + (t.pool - t.lost), 0)
}

const tempo = (f: TwinsFight) => (f.room === 2 ? ROOM2_TEMPO : 1)
const at = (f: TwinsFight, ms: number) => ms * tempo(f)

/** 生まれた時の帯で当てる当たり。swing = 二人の体の振り (ガード・回避の反撃は他のボスの近接と同じ・11-03)。装置の床の技は swing を付けない (位置取りで避ける物)。 */
function band(attackId: string, x0: number, x1: number, parryable: boolean, damage: number, activeMs: number, originX: number, swing: boolean): ActiveHit {
  return {
    id: nextId++, attackId, kind: 'melee', ...(swing ? { swing: true as const } : {}), xStart: Math.min(x0, x1), xEnd: Math.max(x0, x1),
    elapsedMs: 0, telegraphMs: 0, activeMs, damage, postureDamage: parryable ? 10 : 0, parryable,
    hitOnce: true, consumed: false, playerWasHit: false, counterAwarded: false, parryIntensity: parryable ? 0.5 : 0.8,
    originX, originDir: 1,
  }
}

function bullet(attackId: string, x: number, dir: number, y: number, parryable: boolean, damage: number, speed: number, width: number): ActiveHit {
  return {
    id: nextId++, attackId, kind: 'melee', follow: 'travel', x, y, vx: dir * speed, vy: 0, widthPx: width, launchDelayMs: 0,
    xStart: x - width / 2, xEnd: x + width / 2, elapsedMs: 0, telegraphMs: 0, activeMs: 6000,
    damage, postureDamage: parryable ? 8 : 0, parryable,
    hitOnce: true, consumed: false, playerWasHit: false, counterAwarded: false, parryIntensity: 0.4,
  }
}

// ---- 二人 ----
/** 片方がいま赤く光っているか (赤い当たりの前の決まった間だけ・合わせ技では相方の弾く当たりの後から・redGlowFrom)。絵 (twins_fight_draw) とテストが同じ物を見る。 */
export function twinGlowsRed(t: TwinState, nowMs: number): boolean {
  const m = t.move
  if (!m || t.down) return false
  const el = nowMs - t.start
  const i = m.hits.findIndex((x, j) => x.by === t.id && !t.done.has(j))
  if (i < 0 || el < 0 || el >= m.hits[i]!.at) return false
  const from = redGlowFrom(m, i)
  return from !== null && el >= from
}

function partner(f: TwinsFight, t: TwinState): TwinState { return f.twins.find((x) => x !== t)! }

function begin(t: TwinState, m: TwinMove, start: number, dist: number): void {
  t.move = m; t.start = start; t.done = new Set(); t.fromX = t.x; t.told = false
  t.lunge = m.lungePx ? Math.max(0, Math.min(m.lungePx, dist - 70)) : 0
  // 前の技で撃った鈴がまだ飛んでいる間は、その当たりの予約を残す (相方が赤い技をそこへ重ねないように・決まり5・11-03)。
  const flying = t.booked.filter((x) => x.from !== undefined && x.defense === 'parry' && x.t > start - SAME_BEAT_MS)
  // m は think で縮めた写し (2部屋目)。ここでもう一度縮めると予約が実際の当たりより早くずれる (11-03 直し)。
  t.booked = [...flying, ...absHits(m, start, dist).filter((x) => x.by === t.id)]
}

/** 2部屋目は技の時間を縮めた写しを使う。 */
function scaled(f: TwinsFight, m: TwinMove): TwinMove {
  if (tempo(f) === 1) return m
  return { ...m, hits: m.hits.map((h) => ({ ...h, at: at(f, h.at) })), endMs: at(f, m.endMs) }
}

function think(f: TwinsFight, t: TwinState, player: Player, now: number, w: ReturnType<typeof walls>): boolean {
  const p = partner(f, t)
  const partnerBig = !!(p.move && p.move.big && now < p.start + p.move.endMs)
  const dist = Math.abs(player.x - t.x)
  const c = pickable(t.id, partnerBig, p.down)
    .filter((m) => m.owner !== 'both' || t.id === 'ane')
    .filter((m) => dist <= m.needDx)
    .filter((m) => m.id !== 'i_bell' || dist > 160)
  if (c.length === 0) return false
  // 二人が同じ側にいる間は殴らない (挟み直してから)。壁際では挟めないので、その時だけは同じ側からでも殴る。
  if (!p.down && Math.sign(t.x - player.x) === Math.sign(p.x - player.x) && player.x > w.left + 110 && player.x < w.right - 110) return false
  const m = scaled(f, c[Math.floor(rnd() * c.length)]!)
  const partnerHits = p.booked.filter((x) => x.t > now - 300)
  const partnerBigUntil = partnerBig ? p.start + p.move!.endMs : 0
  const start = earliestStart(m, now, partnerHits, partnerBigUntil, dist)
  begin(t, m, start, dist)
  if (m.owner === 'both') begin(p, m, start, dist)
  return true
}

function twinTick(f: TwinsFight, t: TwinState, player: Player, now: number, dt: number, w: ReturnType<typeof walls>, sfx: SfxId[]): void {
  if (t.down) return
  t.face = player.x >= t.x ? 1 : -1
  if (now < f.brokenUntil) { t.move = null; t.booked = []; return }
  const m = t.move
  if (m) {
    const el = now - t.start
    if (el < 0) return
    if (!t.told) {
      t.told = true
      // ?bossSfx=v1: 鈴・号令・滑り・千日手は固有の予告 (二人で出す技は二人とも鳴らす・同じ音は鳴り分けの間隔で1つに)。
      sfx.push(twinsSfxTell(m.id) ?? (m.hits.some((x) => x.by === t.id && x.defense === 'dodge') ? 'telegraph_start' : 'step'))
    }
    if (m.lungePx) t.x = t.fromX + t.face * t.lunge * Math.min(1, el / Math.max(1, m.hits[0]!.at - 100))
    if (m.slide) {
      const hitAt = m.hits[0]!.at, dir = t.fromX < player.x ? 1 : -1
      if (el > hitAt - 350) {
        const k = Math.min(1, (el - (hitAt - 350)) / 600)
        t.x = Math.max(w.left, Math.min(w.right, t.fromX + dir * k * (Math.abs(player.x - t.fromX) + 170)))
      }
    }
    if (m.owner === 'both') {
      const ane = f.twins[0]!
      const side = t.id === 'ane' ? (ane.fromX < player.x ? -1 : 1) : (ane.fromX < player.x ? 1 : -1)
      const want = Math.max(w.left, Math.min(w.right, player.x + side * 95))
      t.x += (want - t.x) * Math.min(1, dt / 160)
    }
    m.hits.forEach((x, i) => {
      if (x.by !== t.id || t.done.has(i) || el < x.at) return
      t.done.add(i)
      const parry = x.defense === 'parry'
      const id = `twins_${m.id}`
      const hitSfx = twinsSfxHit(m.id, x.defense)
      if (hitSfx) sfx.push(hitSfx)
      if (x.kind === 'bullet') { f.pending.push(bullet(id, t.x + t.face * 30, t.face, ARENA.floorY - 90, true, DMG.bullet, BULLET_SPEED, 24)); return }
      if (x.kind === 'wave') { f.pending.push(band(id, t.x - x.reach, t.x + x.reach, false, DMG.dodge, 200, t.x, true)); return }
      if (x.kind === 'pass') { f.pending.push(band(id, t.x - x.reach, t.x + x.reach, false, DMG.dodge, 260, t.x, true)); return }
      // 近接: 体の少し後ろから前へ reach まで (向きのある帯)
      f.pending.push(band(id, t.x - t.face * 20, t.x + t.face * x.reach, parry, parry ? DMG.parry : DMG.dodge, 130, t.x, true))
    })
    if (el >= m.endMs) { t.move = null; t.booked = t.booked.filter((x) => x.t > now); t.nextThink = now + at(f, 350 + rnd() * 500) }
    return
  }
  // 立ち位置: 主人公を二人で左右から挟む。同じ側に寄ったら、手の空いている方が主人公を跳び越えて反対側へ回る。
  //   重ならないように (10-30 作者「位置取りを注意しないと重なって…読みにくくなりそう」):
  //   - 回る時は主人公の頭の上を弧で越える (体を通り抜けない)。
  //   - 壁際で反対側に余地が無い時は回らず、同じ側に間を空けて並ぶ (近い方 100・遠い方 230)。
  //   - 相方が倒れている時は、その体から TWIN_GAP 以上離れて立つ。
  const p = partner(f, t)
  const room = (s: number) => (s < 0 ? player.x - w.left : w.right - player.x)
  const flip = (s: -1 | 1): -1 | 1 => (s === 1 ? -1 : 1)
  const sideOf = (x: number): -1 | 1 => (Math.abs(x - player.x) < 1 ? (room(-1) > room(1) ? -1 : 1) : x < player.x ? -1 : 1)
  const mySide = sideOf(t.x), pSide = sideOf(p.x)
  const pBusy = !!(p.move && now >= p.start)
  let side = mySide, dist = 100
  if (t.hop) side = player.x > t.hop.from ? 1 : -1
  else if (room(mySide) < 70) side = flip(mySide)   // 主人公と壁の間に挟まった: 跳び越えて広い側へ出る
  else if (!p.down && mySide === pSide) {
    const iCross = pBusy || t.id === 'imo' || p.crossing
    if (room(-pSide) < 90) {
      // 壁を背にした主人公: 同じ側で前後に並ぶ。主人公に近い方が前 (100)、後ろは前の者の 130 外側 (追い越さない)。
      const pd = Math.abs(p.x - player.x), td = Math.abs(t.x - player.x)
      if (td > pd || (td === pd && t.id === 'imo')) dist = Math.max(230, pd + 130)
    } else if (iCross && !p.crossing) side = flip(pSide)
  }
  if (t.id === 'imo' && pBusy && dist === 100) dist = 250
  let want = Math.max(w.left, Math.min(w.right, player.x + side * dist))
  if (p.down && Math.abs(want - p.x) < TWIN_GAP) {
    // 倒れた相方の上に立たない: 反対側に余地があればそちらへ、無ければ体の外側へ。
    const other = Math.max(w.left, Math.min(w.right, player.x - side * dist))
    want = Math.abs(other - p.x) >= TWIN_GAP && Math.abs(other - player.x) >= 60 ? other
      : Math.max(w.left, Math.min(w.right, p.x + (want >= p.x ? 1 : -1) * TWIN_GAP))
    if (Math.abs(want - p.x) < TWIN_GAP) want = Math.max(w.left, Math.min(w.right, p.x - (want >= p.x ? 1 : -1) * TWIN_GAP))
  }
  if (!t.hop && side !== mySide) t.hop = { from: t.x, start: now, dur: Math.max(HOP_MIN_MS, Math.abs(want - t.x) / HOP_SPEED * 1000) }
  if (t.hop) {
    const u = Math.min(1, (now - t.hop.start) / t.hop.dur)
    t.x = t.hop.from + (want - t.hop.from) * (u * u * (3 - 2 * u))
    t.crossing = true
    if (u >= 1) { t.hop = null; t.crossing = false }
    return
  }
  t.crossing = false
  // 技の終わりに主人公・相方へ重なって残った時 (壁際の滑り込み・突きの後) や、倒れた相方の上にいる時は、素早く間を空ける。
  const close = Math.abs(t.x - player.x) < 60 || Math.abs(t.x - p.x) < (p.down ? TWIN_GAP : 60)
  const step = (close ? 420 : 150) * dt / 1000
  if (Math.abs(want - t.x) > 6) t.x += Math.sign(want - t.x) * Math.min(step, Math.abs(want - t.x))
  if (now >= t.nextThink && !think(f, t, player, now, w)) t.nextThink = now + 200
}

// ---- 奥の装置 (3部屋目) ----
export function curtainSpan(d: DevAtk, w: { left: number; right: number }): [number, number] {
  const width = 420
  const x0 = Math.max(w.left, Math.min(w.right - width, d.x - width / 2))
  return [x0, x0 + width]
}
export function mapSpan(d: DevAtk, w: { mid: number; left: number; right: number }): [number, number] {
  return d.side < 0 ? [w.left - 40, w.mid + 80] : [w.mid - 80, w.right + 40]
}
export function mapEdgeX(d: DevAtk, w: { mid: number }): number { return d.side < 0 ? w.mid + 80 : w.mid - 80 }

function devBegin(f: TwinsFight, kind: DevKind, player: Player, now: number, w: ReturnType<typeof walls>): void {
  const side: -1 | 1 = player.x < w.mid ? -1 : 1
  const d: DevAtk = { kind, start: now, end: now + 3000, side, x: player.x, hit: false, pieces: [], wallHp: 3, wallUntil: 0, lightX: player.x }
  if (kind === 'march') {
    const from: -1 | 1 = player.x < w.mid ? 1 : -1   // 遠い側の端から出てくる
    d.side = from
    for (let i = 0; i < 3; i++) d.pieces.push({ x: from > 0 ? w.right + 80 : w.left - 80, out: 900 + i * 520, hitId: 0, spawned: false })
    d.end = now + 5200
  }
  if (kind === 'wall') { d.x = Math.max(w.left + 30, Math.min(w.right - 30, player.x + (rnd() < 0.5 ? -1 : 1) * 120)); d.end = now + 5600; d.wallUntil = now + 5600 }
  if (kind === 'map') d.end = now + 3600
  if (kind === 'curtain') d.end = now + 2800
  if (kind === 'light') d.end = now + 3100
  f.dev = d
}

function devTick(f: TwinsFight, player: Player, now: number, dt: number, w: ReturnType<typeof walls>, sfx: SfxId[]): void {
  if (f.devLost >= f.devPool) return
  if (now < f.brokenUntil) return
  if (!f.dev) {
    if (now >= f.devNext) { devBegin(f, DEV_ORDER[f.devIdx++ % DEV_ORDER.length]!, player, now, w); sfx.push(twinsSfxDevice('start') ?? 'telegraph_start') }
    return
  }
  const d = f.dev, el = now - d.start
  if (d.kind === 'march') {
    for (const p of d.pieces) {
      if (p.spawned || el < p.out) continue
      p.spawned = true
      const h = bullet('twins_dev_march', p.x, -d.side, ARENA.floorY - 40, true, DMG.parry, 380, 50)
      p.hitId = h.id; f.marchIds.add(h.id); f.pending.push(h)
      const marchSfx = twinsSfxDevice('march')
      if (marchSfx) sfx.push(marchSfx)
    }
  }
  const slam = (x0: number, x1: number, origin: number) => { d.hit = true; sfx.push(twinsSfxDevice('slam') ?? 'attack_swing_heavy'); f.pending.push(band(`twins_dev_${d.kind}`, x0, x1, false, DMG.device, 160, origin, false)) }
  if (d.kind === 'wall' && !d.hit && el >= DEV_SLAM_MS.wall) slam(d.x - 52, d.x + 52, d.x)
  if (d.kind === 'map' && !d.hit && el >= DEV_SLAM_MS.map) { const [a, b] = mapSpan(d, w); slam(a, b, (a + b) / 2) }
  if (d.kind === 'curtain' && !d.hit && el >= DEV_SLAM_MS.curtain) { const [a, b] = curtainSpan(d, w); slam(a, b, (a + b) / 2) }
  if (d.kind === 'light') {
    if (el < 1600) d.lightX += (player.x - d.lightX) * Math.min(1, dt / 330)
    if (!d.hit && el >= DEV_SLAM_MS.light) slam(d.lightX - 58, d.lightX + 58, d.lightX)
  }
  if (now >= d.end && el >= (d.kind === 'march' ? 900 + 2 * 520 + 2500 : 0)) { f.dev = null; f.devNext = now + 1100 }
}

/** 装置の床の技 (壁・地図・幕・照明) が床を叩くまでの時間 (技の始まりから ms)。 */
export const DEV_SLAM_MS: Record<Exclude<DevKind, 'march'>, number> = { wall: 1120, map: 1300, curtain: 1550, light: 2100 }
/** 装置の床の技の赤い予告 (弾けない・ガードできない＝赤く光らせる・11-03 決まりの外れの直し)。
 *  技の始まりから床を叩くまで、叩く床の幅を返す。行軍の駒は弾ける当たりなので赤くしない (null)。 */
export function devRedSpan(d: DevAtk, w: { left: number; right: number; mid: number }, nowMs: number): [number, number] | null {
  if (d.kind === 'march') return null
  const el = nowMs - d.start
  if (el < 0 || el >= DEV_SLAM_MS[d.kind]) return null
  if (d.kind === 'wall') return [d.x - 52, d.x + 52]
  if (d.kind === 'map') return mapSpan(d, w)
  if (d.kind === 'curtain') return curtainSpan(d, w)
  return [d.lightX - 58, d.lightX + 58]
}

/** 3部屋目で斬れる所 (落ちた守りの駒・叩きつけた地図の縁)。 */
function devTarget(f: TwinsFight, now: number, w: ReturnType<typeof walls>): { kind: 'wall' | 'map'; x: number } | null {
  const d = f.dev
  if (!d) return null
  const el = now - d.start
  if (d.kind === 'wall' && el >= 1120 && d.wallHp > 0 && now < d.wallUntil) return { kind: 'wall', x: d.x }
  if (d.kind === 'map' && el >= 1300 && el < 3500) return { kind: 'map', x: mapEdgeX(d, w) }
  return null
}

const FAR_AWAY = -100000

/** このコマは updateBoss の代わりに二人を動かすか (崩れて致命を待つ・致命の間・撃破の後は updateBoss のまま)。 */
export function twinsDriveThisTick(boss: Boss): boolean {
  return twinsFightOn(boss) && boss.state !== 'staggered' && boss.hp > 0
}

/** 毎コマ、updateBoss の代わりに呼ぶ。二人 (装置) を動かし、boss を「いま斬れる所」へ置く。 */
export function twinsBossTick(boss: Boss, player: Player, nowSec: number, sfx: SfxId[]): Boss {
  const now = nowSec * 1000
  const f = ensure(boss, player, now)
  const dt = Math.max(0, Math.min(100, now - f.lastNow))
  if (f.reveal) {
    const prev = f.lastNow - f.reveal.start, e = now - f.reveal.start
    const cross = (ms: number): boolean => prev < ms && e >= ms
    // C の現れ方の音 (11-01・src/boss_sfx_v1.ts twinsSfxReveal)。山を合図 (swallow・appear) に合わせて先に鳴らし始める。
    const suck = revealLook === 'drop' ? twinsSfxReveal('suck') : null
    const land = revealLook === 'drop' ? twinsSfxReveal('land') : null
    if (suck && cross(REVEAL_DROP.swallow - TWINS_REVEAL_SFX_PEAK_MS.suck)) sfx.push(suck)
    if (land && cross(REVEAL_DROP.appear - TWINS_REVEAL_SFX_PEAK_MS.land)) sfx.push(land)
    // 装置が現れた瞬間。着地の音が無い時 (前の光の現れ・?twinsRevealSfx=off) は仮の重い一音。
    if (cross(revealAppearMs())) {
      if (!land) sfx.push('hit_boss_heavy')
      if (revealLook === 'drop') revealShakeAt = now
    }
  }
  f.lastNow = now
  const w = walls(boss)
  if (planA && f.room !== 3) planATick(f, now)
  for (const t of f.twins) twinTick(f, t, player, now, dt, w, sfx)
  if (f.room === 3) devTick(f, player, now, dt, w, sfx)
  return placeBoss(f, { ...boss, state: 'idle', currentAttack: null, currentString: null, stateElapsed: boss.stateElapsed + dt / 1000,
    postExecuteInvincMs: Math.max(0, boss.postExecuteInvincMs - dt) }, player, now, w)
}

/** 案A: 総崩れの終わり・倒れた一人の起き上がり。 */
function planATick(f: TwinsFight, now: number): void {
  if (f.collapseUntil > 0) {
    if (now < f.collapseUntil) return
    f.collapseUntil = 0
    for (const t of f.twins) rise(t, now)
    return
  }
  for (const t of f.twins) if (t.down && t.downUntil > 0 && now >= t.downUntil) rise(t, now)
}
function rise(t: TwinState, now: number): void {
  t.down = false; t.downUntil = 0; t.move = null; t.booked = []
  t.lost = Math.min(t.lost, t.pool * (1 - PLAN_A.riseBack))
  t.nextThink = now + 700
}
/** 案A の総崩れの間か (描画用)。 */
export function twinsCollapsed(nowSec: number): boolean { return !!fight && nowSec * 1000 < fight.collapseUntil }

function placeBoss(f: TwinsFight, boss: Boss, player: Player, now: number, w: ReturnType<typeof walls>): Boss {
  let x = FAR_AWAY
  f.target = null
  if (f.room === 3) {
    const tg = devTarget(f, now, w)
    if (tg) { x = tg.x; f.target = { kind: tg.kind } }
  } else {
    const up = f.twins.filter((t) => !t.down && !t.hop).length ? f.twins.filter((t) => !t.down && !t.hop) : f.twins.filter((t) => !t.down)
    const near = (up.length ? up : f.twins).reduce((a, b) => (Math.abs(a.x - player.x) <= Math.abs(b.x - player.x) ? a : b))
    x = near.x
    f.target = { kind: 'twin', id: near.id }
  }
  // 斬れる所が無い時は遠くへ (主人公の太刀が届かない)。見た目は描画が二人・装置を描くので影響しない。
  if (x === FAR_AWAY) x = player.x + (player.x < w.mid ? 1 : -1) * 4000
  return { ...boss, x, facing: player.x >= x ? 'right' : 'left' }
}

/** game.ts が `let hits = state.hits` の後に足す。 */
export function twinsTakeHits(): ActiveHit[] {
  if (!fight || fight.pending.length === 0) return []
  const out = fight.pending
  fight.pending = []
  return out
}

/**
 * 当たりを解いた後に呼ぶ。主人公の削りを「いま斬れる所」へ割り振り、hp・体幹・崩れ・膝つきを整える。
 *   prev = このコマの頭の boss (= 削られる前)。hits = 当たりを解いた後のヒット (行軍の駒を弾いたかを見る)。
 */
export function twinsAfterHits(prev: Boss, boss: Boss, player: Player, nowSec: number, hits: readonly ActiveHit[], staggerBoss: (b: Boss) => Boss, sfx?: SfxId[]): Boss {
  const f = fight
  if (!f || prev.state === 'staggered' || boss.hp <= 0 || f.roomKey !== boss.executesTaken) return boss
  const now = nowSec * 1000
  const w = walls(boss)
  const broken = now < f.brokenUntil
  const struck = boss.hp < prev.hp
  const floor = chipFloor(boss)
  if (planA && f.room !== 3) return planAAfterHits(f, prev, boss, player, now, hits, floor, staggerBoss, sfx)
  if (f.room === 3) {
    // 斬れる部品へ当てた・行軍の駒を弾いた分だけ装置が減る (ラボの devHp60 の比率)。
    const unit = f.devPool / 60
    if (struck && f.target && f.dev) {
      if (f.target.kind === 'wall') {
        f.dev.wallHp--; f.devLost += 4 * unit
        if (f.dev.wallHp <= 0) { f.dev.wallUntil = now; f.devLost += 6 * unit }
      } else f.devLost += 5 * unit
    }
    for (const h of hits) {
      if (f.marchIds.has(h.id) && (h.consumed || h.reflected) && !h.playerWasHit) { f.marchIds.delete(h.id); f.devLost += 3 * unit }
    }
    f.devLost = Math.min(f.devPool, f.devLost)
    boss = { ...boss, hp: Math.max(floor, floor + f.devPool - f.devLost) }
  } else {
    if (struck && f.target?.kind === 'twin') {
      const id = f.target.id
      const t = f.twins.find((x) => x.id === id)!
      t.lost = Math.min(t.pool, t.lost + t.pool * 2 * TWIN_HIT_SHARE * (broken ? TWINS_BREAK_DAMAGE_MUL : 1))
      if (t.lost >= t.pool - 1e-6 && !t.down) { t.down = true; t.move = null; t.booked = [] }
    }
    boss = { ...boss, hp: Math.max(floor, floor + remainOf(f)) }
  }
  // 体幹が満ちて崩れた・まだ hp が残る → 致命ではなく「崩れ」(2.6秒・削りが3倍)。
  if (boss.state === 'staggered' && boss.hp > floor) {
    f.brokenUntil = now + TWINS_BREAK_MS
    for (const t of f.twins) { t.move = null; t.booked = [] }
    if (f.dev) { f.dev = null; f.devNext = now + TWINS_BREAK_MS + 600 }
    boss = { ...boss, state: 'idle', stateElapsed: 0, posture: 0 }
  }
  // 二人とも倒れた (装置が壊れた) → 普段の崩れ → 致命へ。
  if (boss.hp <= floor && boss.state !== 'staggered') boss = staggerBoss({ ...boss, hp: floor })
  return boss.state === 'staggered' ? boss : placeBoss(f, boss, player, now, w)
}

/** 案A の1・2部屋目。体力は二人の持ち分を減らすだけで致命には繋げず、致命は体幹が満ちた時だけ。 */
function planAAfterHits(f: TwinsFight, prev: Boss, boss: Boss, player: Player, now: number, hits: readonly ActiveHit[], floor: number, staggerBoss: (b: Boss) => Boss, sfx?: SfxId[]): Boss {
  const w = walls(boss)
  // 弾き返した鈴: 前のコマに飛んでいて、壁の内側で消えた物はボスに当たった (hits.ts が体力 6・体幹を足して消す)。体力の分は戻す。
  const alive = new Map<number, number>()
  for (const h of hits) {
    if (h.follow === 'travel' && h.reflected && !h.consumed && h.attackId.startsWith('twins_') && h.attackId !== 'twins_dev_march' && h.x !== undefined) alive.set(h.id, h.x)
  }
  let bellHits = 0
  for (const [id, x] of f.bells) if (!alive.has(id) && x > w.left - 20 && x < w.right + 20) bellHits++
  f.bells = alive
  const drop = Math.max(0, prev.hp - boss.hp)
  const swordDrop = Math.max(0, drop - bellHits * REFLECTED_BELL_HP)
  const collapsed = now < f.collapseUntil
  let posture = boss.posture
  if (collapsed) {
    // 総崩れの間は体幹が戻らない。斬るたびに大きく貯まる。
    posture = Math.max(posture, prev.posture)
    if (swordDrop > 0) posture += boss.maxPosture * PLAN_A.collapsePosture
  }
  if (swordDrop > 0 && f.target?.kind === 'twin') {
    const id = f.target.id
    const t = f.twins.find((x) => x.id === id)!
    if (!t.down) {
      t.lost = Math.min(t.pool, t.lost + t.pool * 2 * TWIN_HIT_SHARE)
      if (t.lost >= t.pool - 1e-6) {
        t.down = true; t.move = null; t.booked = []; t.downUntil = now + PLAN_A.downMs
        if (f.twins.every((x) => x.down)) {
          f.collapseUntil = now + PLAN_A.collapseMs
          for (const x of f.twins) x.downUntil = f.collapseUntil
        }
      }
    }
  }
  posture = Math.min(boss.maxPosture, posture)
  // 体力は床の1つ上で止める (床に届くと致命の扉が開くため)。
  boss = { ...boss, hp: floor + Math.max(1, remainOf(f)), posture }
  // 総崩れの上乗せで満ちた時は、ここで崩す (当たりの処理は上乗せの前に崩れを見ている)。
  if (boss.state !== 'staggered' && boss.posture >= boss.maxPosture) {
    boss = staggerBoss(boss)
    sfx?.push('stagger')
  }
  if (boss.state === 'staggered') {
    if (boss.posture >= boss.maxPosture) {
      // 体幹で崩れた → そのまま致命へ (崩れの2.6秒に変えない)。二人の技は止める。
      f.collapseUntil = 0
      for (const t of f.twins) { t.move = null; t.booked = [] }
      return boss
    }
    // 体力が床に届いて開いた崩れは取り消す (致命は体幹だけ)。
    boss = { ...boss, state: 'idle', stateElapsed: 0 }
    if (sfx) for (let i = sfx.length - 1; i >= 0; i--) if (sfx[i] === 'stagger') sfx.splice(i, 1)
  }
  return placeBoss(f, boss, player, now, w)
}

/** 3部屋目の装置がもう見えているか (現れの途中は見えない)。 */
export function twinsDeviceShown(f: TwinsFight, now: number): boolean {
  return f.room === 3 && (!f.reveal || now >= f.reveal.start + revealAppearMs())
}

/** 幕の印 (HUD) の数と点き方。1・2部屋目と3部屋目の現れの前は2つに見せ、3幕目は現れた時に3つ目として右に加わる
 *  (10-30 作者「最初は2幕にしか見えないようにしてほしい」)。lamp_snuff の印の番号もここで置き換える。 */
export function twinsActMarks(boss: Boss, nowSec: number): { count: number; lit: boolean[]; snuffIndex: (i: number) => number } | null {
  if (!twinsFightOn(boss)) return null
  const f = fight
  const shown = !!f && f.roomKey === boss.executesTaken && twinsDeviceShown(f, nowSec * 1000)
  if (!shown) {
    const lit = Math.max(0, 2 - boss.executesTaken)
    return { count: 2, lit: [0, 1].map((i) => i < lit), snuffIndex: (i) => i - 1 }
  }
  return { count: 3, lit: [false, false, boss.executesTaken < 3], snuffIndex: () => 2 }
}

/** 崩れの間か (描画用)。 */
export function twinsBroken(nowSec: number): boolean { return !!fight && nowSec * 1000 < fight.brokenUntil }
